import {
    ComponentProps,
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { AnalyticsFiltersContainer, DrawerWrapper } from '@/components'
import {
    GetReportResponse,
    BenchmarkingReportSettings,
    CustomReportFiltersParams,
    BenchmarkingBasis,
    useElementWidth,
    useFiltersStore,
} from '@/hooks'
import { ReportEditHeader } from './ReportEditHeader'
import { mapFiltersToParams } from './mapFiltersToParams'
import { mapParamsToFilters } from './mapParamsToFilters'
import useNavigationGuardStore from '@/stores/useNavigationGuardStore/useNavigationGuardStore'
import {
    hasNameChanged,
    haveFiltersChanged,
} from '../../utils/detectReportChange'
import { processBenchmarkingFilters } from '../../utils/processBenchmarkingFilters'
import { BenchmarkingReport } from '@/features/Analytics/components/BenchmarkingReport/BenchmarkingReport'
import { BenchmarkingDrawer } from '@/components/data/Benchmarking/BenchmarkingDrawer'
import { mapBenchmarkingParamsToReportSettings } from '@/components/data/Benchmarking/mapBenchmarkingParamsToReportSettings'
import {
    DEFAULT_ANALYTICS_FILTERING,
    DEFAULT_BENCHMARKING_REPORT_SETTINGS,
} from '@/utils/constants'
import { TableLoadingOverlay } from '@/components'
import { useTranslation } from 'react-i18next'

type ReportEditBenchmarkingProps = {
    data: GetReportResponse
    isOpen: boolean
    setIsOpen: Dispatch<SetStateAction<boolean>>
}

export const ReportEditBenchmarking: React.FC<ReportEditBenchmarkingProps> = (
    props
) => {
    const { t } = useTranslation('translations')

    const [data, setData] = useState(props.data)
    const [filters, setFilters] = useState(data.filters)
    const [name, setName] = useState(data.name)
    const [basis, setBasis] = useState<BenchmarkingBasis>(
        data.settings &&
            typeof (data.settings as BenchmarkingReportSettings).basis ===
                'string'
            ? (data.settings as BenchmarkingReportSettings).basis
            : DEFAULT_BENCHMARKING_REPORT_SETTINGS.basis
    )
    const toggleButtonRef = useRef<HTMLButtonElement>(null)
    const toggleWidth = useElementWidth(toggleButtonRef)

    const parsedFilters = mapParamsToFilters(filters)
    const benchmarkingRequestParams = processBenchmarkingFilters(filters)
    const rootNode = useFiltersStore((state) => state.rootNode)

    const reportSettings = useMemo(
        () => ({
            ...mapBenchmarkingParamsToReportSettings(benchmarkingRequestParams),
            basis,
        }),
        [benchmarkingRequestParams, basis]
    )

    const baselineFilters = useMemo(() => {
        const params = mapFiltersToParams({
            ...DEFAULT_ANALYTICS_FILTERING,
            node: rootNode?.nodeId !== undefined ? [rootNode.nodeId] : [],
        })

        const { commodityTypeIds: _commodityTypeIds, ...filtered } = params
        return filtered
    }, [rootNode?.nodeId])

    const handleResetFilters = useCallback(() => {
        setFilters(baselineFilters)
    }, [baselineFilters])

    const showResetFiltersButton = useMemo(() => {
        return haveFiltersChanged(filters, baselineFilters)
    }, [filters, baselineFilters])

    const updatedReport = useMemo(
        () => ({
            ...data,
            name,
            filters,
        }),
        [data, filters, name]
    )

    const setUnsavedChanges = useNavigationGuardStore(
        (state) => state.setUnsavedChanges
    )

    useEffect(() => {
        const filtersChanged = haveFiltersChanged(filters, data.filters)
        const nameChanged = hasNameChanged(name, data.name)

        setUnsavedChanges(filtersChanged || nameChanged)
    }, [name, filters, data.name, data.fields, data.filters, setUnsavedChanges])

    const handleReportSaved = () => {
        setFilters(updatedReport.filters)
        setName(updatedReport.name)
        setData(updatedReport)
        setUnsavedChanges(false)
    }

    const includeReportSettingsParams = useCallback(
        (
            params: CustomReportFiltersParams,
            reportSettings: BenchmarkingReportSettings
        ) => {
            if (reportSettings.basis === 'emission') {
                params.includeEmissions = true
            } else {
                delete params.includeEmissions
            }

            params.type = reportSettings.display
            params.trendType = reportSettings.analysisType

            if (reportSettings.analysisType !== 'trend') {
                params.comparisonPeriod =
                    reportSettings.comparisonPeriod ||
                    DEFAULT_BENCHMARKING_REPORT_SETTINGS.comparisonPeriod
            } else {
                delete params.comparisonPeriod
            }

            if (reportSettings.applyWeatherNormalization) {
                params.applyWeatherNormalization =
                    reportSettings.applyWeatherNormalization
            } else {
                delete params.applyWeatherNormalization
            }

            if (reportSettings.applySquareFootage) {
                params.applySquareFootage = reportSettings.applySquareFootage
            } else {
                delete params.applySquareFootage
            }

            return params
        },
        []
    )

    const onChangeReportSettings = useCallback(
        (reportSettings: BenchmarkingReportSettings) => {
            setBasis(reportSettings.basis)
            setFilters((prevParams) => {
                const prevFilters = mapParamsToFilters(prevParams)
                const newFilters = {
                    ...prevFilters,
                    ...reportSettings,
                }
                const newParams = mapFiltersToParams(newFilters)
                const reportSettingsParams = includeReportSettingsParams(
                    newParams,
                    reportSettings
                )
                return reportSettingsParams
            })
        },
        [includeReportSettingsParams]
    )

    const onFiltersChange = useCallback<OnChangeFn>(
        (filter, value) => {
            setFilters((prevParams) => {
                const prevFilters = mapParamsToFilters(prevParams)

                const newFilters = {
                    ...prevFilters,
                    [filter]: value,
                }
                const newParams = mapFiltersToParams(newFilters)
                const reportSettingsParams = includeReportSettingsParams(
                    newParams,
                    reportSettings
                )

                reportSettingsParams.commodityTypeId =
                    newParams.commodityTypeId ?? newParams.commodityTypeIds?.[0]

                return reportSettingsParams
            })
        },
        [reportSettings, includeReportSettingsParams]
    )

    const selectedFilter = useMemo(() => {
        if (reportSettings.display === 'group') {
            return 'tag'
        }

        return reportSettings.display
    }, [reportSettings.display])

    const updatedReportWithSettings = useMemo(
        () => ({
            ...updatedReport,
            settings: {
                ...(updatedReport.settings as BenchmarkingReportSettings),
                basis,
            },
        }),
        [updatedReport, basis]
    )

    const calculationMethodOffset = props.isOpen ? 16 : toggleWidth

    const [isUpdatePending, setIsUpdatePending] = useState(false)

    return (
        <div className="relative grid h-full grid-cols-[100%] grid-rows-[auto_1fr]">
            <div className="relative z-30">
                <ReportEditHeader
                    initialReportName={data.name}
                    report={updatedReportWithSettings}
                    onChangeName={(reportName: string) => {
                        setName(reportName)
                    }}
                    onSaveAsNewReportSuccess={handleReportSaved}
                    onUpdatePendingChange={setIsUpdatePending}
                />
                <div className="border-b border-neutral-200 p-6">
                    <AnalyticsFiltersContainer
                        activeFilters={parsedFilters}
                        onChange={onFiltersChange}
                        isBenchmarking={data.type === 'benchmarking'}
                        selectedFilter={selectedFilter}
                        handleReset={handleResetFilters}
                        showResetFiltersButton={showResetFiltersButton}
                    />
                </div>
            </div>
            <TableLoadingOverlay
                show={isUpdatePending}
                title={t('reporting.update_report.loading_save')}
                coverContainer
            />
            <DrawerWrapper className="h-full">
                <BenchmarkingDrawer
                    filters={benchmarkingRequestParams}
                    reportSettings={reportSettings}
                    setReportSettings={onChangeReportSettings}
                    isOpen={props.isOpen}
                    setIsOpen={props.setIsOpen}
                    toggleButtonRef={toggleButtonRef}
                />
                <BenchmarkingReport
                    filters={benchmarkingRequestParams}
                    reportSettings={reportSettings}
                    isOpen={props.isOpen}
                    calculationMethodOffset={calculationMethodOffset}
                />
            </DrawerWrapper>
        </div>
    )
}

type OnChangeFn = ComponentProps<typeof AnalyticsFiltersContainer>['onChange']
